'use client';

import React, { useEffect, useRef } from 'react';
import Script from 'next/script';

interface AdEmbedProps {
  mode: 'image' | 'embed' | 'adsense';
  label?: string | null;
  imageUrl?: string | null;
  targetUrl?: string | null;
  embedCode?: string | null;
  adsenseClient?: string | null;
  adsenseSlot?: string | null;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export default function AdEmbed({
  mode,
  label,
  imageUrl,
  targetUrl,
  embedCode,
  adsenseClient,
  adsenseSlot,
}: AdEmbedProps) {
  const adRef = useRef<HTMLModElement | null>(null);

  useEffect(() => {
    if (mode !== 'adsense' || !adsenseClient || !adsenseSlot || !adRef.current) {
      return;
    }

    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
    } catch {
      // Ignore adsense hydration/runtime failures.
    }
  }, [adsenseClient, adsenseSlot, mode]);

  if (mode === 'image' && imageUrl) {
    const content = (
      <img data-component-id="src/components/advertising/AdEmbed.tsx:48:6" data-component-path="src/components/advertising/AdEmbed.tsx" data-component-line="48" data-component-end-line="52" data-component-file="AdEmbed.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3AimageUrl%5D%22%2C%22className%22%3A%22h-auto%20w-full%20rounded-2xl%20object-cover%22%7D"
        src={imageUrl}
        alt={label || 'Advertisement'}
        className="h-auto w-full rounded-2xl object-cover"
      />
    );

    return targetUrl ? (
      <a data-component-id="src/components/advertising/AdEmbed.tsx:56:6" data-component-path="src/components/advertising/AdEmbed.tsx" data-component-line="56" data-component-end-line="58" data-component-file="AdEmbed.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%5Bvar%3AtargetUrl%5D%22%2C%22className%22%3A%22block%22%7D" href={targetUrl} target="_blank" rel="noreferrer" className="block">
        {content}
      </a>
    ) : (
      content
    );
  }

  if (mode === 'embed' && embedCode) {
    return <div data-component-id="src/components/advertising/AdEmbed.tsx:65:11" data-component-path="src/components/advertising/AdEmbed.tsx" data-component-line="65" data-component-end-line="65" data-component-file="AdEmbed.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22overflow-hidden%20rounded-2xl%20%5B%26_iframe%5D%3Aw-full%22%7D" className="overflow-hidden rounded-2xl [&_iframe]:w-full" dangerouslySetInnerHTML={{ __html: embedCode }} />;
  }

  if (mode === 'adsense' && adsenseClient && adsenseSlot) {
    return (
      <>
        <Script data-component-id="src/components/advertising/AdEmbed.tsx:71:8" data-component-path="src/components/advertising/AdEmbed.tsx" data-component-line="71" data-component-end-line="77" data-component-file="AdEmbed.tsx" data-component-name="Script" data-component-content="%7B%22elementName%22%3A%22Script%22%7D"
          id={`adsense-script-${adsenseSlot}`}
          async
          strategy="afterInteractive"
          src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseClient}`}
          crossOrigin="anonymous"
        />
        <ins data-component-id="src/components/advertising/AdEmbed.tsx:78:8" data-component-path="src/components/advertising/AdEmbed.tsx" data-component-line="78" data-component-end-line="86" data-component-file="AdEmbed.tsx" data-component-name="ins" data-component-content="%7B%22elementName%22%3A%22ins%22%2C%22className%22%3A%22adsbygoogle%20block%20min-h-%5B280px%5D%20w-full%20overflow-hidden%20rounded-2xl%22%7D"
          ref={adRef}
          className="adsbygoogle block min-h-[280px] w-full overflow-hidden rounded-2xl"
          style={{ display: 'block' }}
          data-ad-client={adsenseClient}
          data-ad-slot={adsenseSlot}
          data-ad-format="auto"
          data-full-width-responsive="false"
        />
      </>
    );
  }

  return (
    <div data-component-id="src/components/advertising/AdEmbed.tsx:92:4" data-component-path="src/components/advertising/AdEmbed.tsx" data-component-line="92" data-component-end-line="94" data-component-file="AdEmbed.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border%20border-dashed%20border-slate-300%20px-4%20py-10%20text-center%20text-xs%20text-slate-400%20dark%3Aborder-slate-700%20dark%3Atext-slate-500%22%2C%22textContent%22%3A%22No%20advert%20configured%22%7D" className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-xs text-slate-400 dark:border-slate-700 dark:text-slate-500">
      No advert configured
    </div>
  );
}
